// react
import React, { useState, Fragment, useEffect } from "react";
// store
import { sharedAccessService } from "../../../store/services/ServiceSharedAccess";
import eventBus from "../../../store/services/EventBus";
// components
import { Form, Grid, Header, Table, Button, Modal, TextArea, Segment, Message, Loader } from "semantic-ui-react";
import { toast } from "../../../components/others/ToastContainer";

// component
function ProfileSharedAccess() {
    // state
    const [pendingRequests, setPendingRequests] = useState([]);
    const [approvedRequests, setApprovedRequests] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [note, setNote] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // effects
    useEffect(() => {
        loadRequests();
    }, []);

    // handlers
    const loadRequests = async () => {
        setIsLoading(true);
        try {
            const pendingResponse = await sharedAccessService.getSharedAccessRequests("?status=pending");
            const approvedResponse = await sharedAccessService.getSharedAccessRequests("?status=approved");

            if (pendingResponse.status === 200) {
                setPendingRequests(pendingResponse.response);
            }

            if (approvedResponse.status === 200) {
                setApprovedRequests(approvedResponse.response);
            }
        } catch (error) {
            toast({ type: 'error', message: 'Nepodarilo sa načítať údaje' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleActionClick = (request, type) => {
        setSelectedRequest(request);
        setModalType(type);
        setIsModalOpen(true);
        setNote('');
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedRequest(null);
        setModalType('');
        setNote('');
    };

    const handleModalConfirm = async () => {
        try {
            if (modalType === 'release') {
                await sharedAccessService.releaseSharedAccess(selectedRequest.id, { message: note });
                toast({ type: 'success', message: 'Prístup úspešne odstránený' });
                eventBus.publish('sharedAccessUpdate');
            } else if (modalType === 'approve') {
                await sharedAccessService.approveSharedAccess(selectedRequest.id, { response_message: note });
                toast({ type: 'success', message: 'Žiadosť schválená úspešne' });
                // Notify about shared access update using EventBus
                eventBus.publish('sharedAccessUpdate');
            } else if (modalType === 'deny') {
                await sharedAccessService.denySharedAccess(selectedRequest.id, { response_message: note });
                toast({ type: 'success', message: 'Žiadosť zamietnutá úspešne' });
                // Notify about shared access update using EventBus
                eventBus.publish('sharedAccessUpdate');
            }

            handleModalClose();
            loadRequests();
        } catch (error) {
            toast({
                type: 'error',
                message: `Nepodarilo sa ${modalType === 'release' ? 'zrušiť' : modalType === 'approve' ? 'schváliť' : 'zamietnuť'} žiadosť`
            });
        }
    };

    // template
    return (
        <Fragment>
            <Grid>
                <Grid.Column computer={16} tablet={16} mobile={16}>
                    <Header as="h3">VZDIALENÝ PRÍSTUP</Header>

                    {isLoading ? (
                        <Segment basic style={{ minHeight: '200px' }}>
                            <Loader active>Načítavam údaje</Loader>
                        </Segment>
                    ) : (
                        <>
                            {/* Pending Requests Section */}
                            {pendingRequests.length > 0 && (
                                <Segment raised color="yellow">
                                    <Header as="h4" color="orange">
                                        Čakajúce žiadosti ({pendingRequests.length})
                                        <Header.Subheader>
                                            Tieto žiadosti čakajú na vaše schválenie
                                        </Header.Subheader>
                                    </Header>
                                    <Table celled>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.HeaderCell>Administrátor</Table.HeaderCell>
                                                <Table.HeaderCell>Dátum žiadosti</Table.HeaderCell>
                                                <Table.HeaderCell>Akcie</Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {pendingRequests.map(request => (
                                                <Table.Row key={request.id} warning>
                                                    <Table.Cell>
                                                        <strong>{request.admin_name}</strong> ({request.admin_email}) <br />

                                                        {request.message && (
                                                            <span style={{ color: 'var(--dark)', display: 'block', marginTop: '0.5rem' }}>
                                                                Poznámka: {request.message}
                                                            </span>
                                                        )}

                                                    </Table.Cell>
                                                    <Table.Cell>{new Date(request.created_at).toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' })}</Table.Cell>
                                                    <Table.Cell width={3}>
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <Button
                                                                positive
                                                                size="tiny"
                                                                onClick={() => handleActionClick(request, 'approve')}
                                                                style={{ flex: 1 }}
                                                            >
                                                                Schváliť
                                                            </Button>
                                                            <Button
                                                                negative
                                                                size="tiny"
                                                                onClick={() => handleActionClick(request, 'deny')}
                                                                style={{ flex: 1 }}
                                                            >
                                                                Zamietnuť
                                                            </Button>
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                </Segment>
                            )}

                            {/* Approved Requests Section */}
                            {approvedRequests.length > 0 ? (
                                <Segment raised color="green">
                                    <Header as="h4" color="green">
                                        Aktívne prístupy ({approvedRequests.length})
                                        <Header.Subheader>
                                            Zoznam aktívnych vzdialených prístupov
                                        </Header.Subheader>
                                    </Header>
                                    <Table celled>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.HeaderCell>Administrátor</Table.HeaderCell>
                                                <Table.HeaderCell>Dátum schválenia</Table.HeaderCell>
                                                <Table.HeaderCell>Akcie</Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>
                                            {approvedRequests.map(request => (
                                                <Table.Row key={request.id} positive>
                                                    <Table.Cell>
                                                        <strong>{request.admin_name}</strong> ({request.admin_email})
                                                    </Table.Cell>
                                                    <Table.Cell>{new Date(request.response_date).toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' })}</Table.Cell>
                                                    <Table.Cell width={3}>
                                                        <Button
                                                            negative
                                                            size="tiny"
                                                            onClick={() => handleActionClick(request, 'release')}
                                                            fluid
                                                        >
                                                            Odobrať prístup
                                                        </Button>
                                                    </Table.Cell>
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    </Table>
                                </Segment>
                            ) : (
                                <Message info>
                                    <Message.Header>Žiadne aktívne prístupy</Message.Header>
                                    <p>Momentálne nie sú žiadne aktívne vzdialené prístupy</p>
                                </Message>
                            )}
                        </>
                    )}

                    {/* Action Modal */}
                    <Modal open={isModalOpen} onClose={handleModalClose} size="tiny">
                        <Modal.Header>
                            {modalType === 'approve' && 'Schváliť žiadosť'}
                            {modalType === 'deny' && 'Zamietnuť žiadosť'}
                            {modalType === 'release' && 'Odobrať prístup'}
                        </Modal.Header>
                        <Modal.Content>
                            {modalType === 'release' ? (
                                <p>Naozaj si želáte odobrať prístup?</p>
                            ) : (
                                <Form>
                                    <Form.Field>
                                        <label>Poznámka</label>
                                        <TextArea
                                            placeholder="Pridať poznámku..."
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                    </Form.Field>
                                </Form>
                            )}
                        </Modal.Content>
                        <Modal.Actions>
                            <Button onClick={handleModalClose}>
                                Zrušiť
                            </Button>
                            <Button positive onClick={handleModalConfirm}>
                                Potvrdiť
                            </Button>
                        </Modal.Actions>
                    </Modal>
                </Grid.Column>
            </Grid>
        </Fragment>
    );
}

export default ProfileSharedAccess;
